import type {QueuedLogRecord} from "@apps/qset-log-batcher/policy";
import type {ActiveQset} from "./activeQset";

/**
 * Lines of the feedback report (#1249) that describe where the respondent
 * was and what the page was doing when they wrote it. Each one answers a
 * question the reader of a report otherwise has to reconstruct from the log
 * by hand; every one degrades to `unavailable` rather than failing the POST.
 */

export interface RtdbNode {
  slug: string;
  userId: string;
}

/**
 * The respondent's answer record, next to the `Qset log:` node: the same slug
 * and user, so the reader opens the answers (and `behavior`) without looking
 * them up.
 */
export const buildResultsLine = (
  dbUrl: string,
  node: RtdbNode | undefined,
): string =>
  node
    ? `Qset result: ${dbUrl.replace(/\/+$/, "")}/results/${node.slug}/${node.userId}`
    : "Qset result: unavailable";

const lastQuestionEnter = (
  records: ReadonlyArray<QueuedLogRecord>,
): QueuedLogRecord | undefined =>
  // Not `findLast`: the app targets ES2021, and Safari before 15.4 lacks it.
  [...records].reverse().find((record) => record.message === "question-enter");

interface QuestionProgressInput {
  records: ReadonlyArray<QueuedLogRecord>;
  entry: ActiveQset | undefined;
  questionId: string | null | undefined;
  pollSlug: string | undefined;
  now: number;
}

/**
 * `Question position:` and `Time on question:`. Both come from the last
 * `question-enter` when it names this question: its `index` is the order this
 * respondent saw after randomization. Without one (v3, which does not log, or
 * a buffer that has rolled past it) the position falls back to the authored
 * qset order and says so, because with randomization the two differ; the
 * time has no fallback.
 */
export const formatQuestionProgressLines = ({
  records,
  entry,
  questionId,
  pollSlug,
  now,
}: QuestionProgressInput): string => {
  const questions =
    entry && entry.slug === pollSlug ? entry.qset.questions : undefined;
  const total = questions?.length;
  const enter = lastQuestionEnter(records);
  const logged =
    questionId && enter?.data?.questionId === questionId ? enter : undefined;
  const loggedIndex = logged?.data?.index;

  let position = "unavailable";
  if (typeof loggedIndex === "number" && total) {
    position = `${loggedIndex + 1} of ${total}`;
  } else if (questions && questionId) {
    const authored = questions.findIndex(
      (q) => (q as {id?: unknown}).id === questionId,
    );
    if (authored >= 0)
      position = `${authored + 1} of ${total} (authored order)`;
  }

  const time = logged
    ? `${((now - logged.timestamp) / 1000).toFixed(1)} s`
    : "unavailable (no question-enter in the log)";

  return `Question position: ${position}\nTime on question: ${time}`;
};

/**
 * A question keeps at most 40 events plus 60 `video-progress` samples
 * (qsetLogBatcherPolicy), so 100 ids cover a whole question short of the
 * every-fifth sampling past the caps, at about 2 kB of report.
 */
export const MAX_LOG_IDS = 100;

/**
 * `Question log IDs (<question>, <count>):` -- the RTDB push ids of this question's
 * events, i.e. the keys under the `Qset log:` node, so the reader opens the
 * exact events instead of filtering the whole node. Taken from the batcher's
 * local buffer after the flush (its last 200 events), so it lists what this
 * device logged; when the flush failed, the newest ids may not be in RTDB yet.
 */
export const formatLogIdsLine = (
  records: ReadonlyArray<QueuedLogRecord>,
  questionId: string | null | undefined,
): string => {
  if (!questionId) return "Question log IDs: unavailable (no-question-id)";
  const ids = records
    .filter((record) => record.data?.questionId === questionId)
    .map((record) => record.id);
  if (ids.length === 0) {
    return `Question log IDs: unavailable (no events for ${questionId} in the local buffer)`;
  }
  // The newest ids are kept: the events right before the report are the
  // ones a stall report is about.
  const shown = ids.slice(-MAX_LOG_IDS);
  const rest = ids.length - shown.length;
  return `Question log IDs (${questionId}, ${ids.length}): ${
    rest > 0 ? `${rest} earlier left out, ... ` : ""
  }${shown.join(", ")}`;
};

const collectVideos = (root: ParentNode): HTMLVideoElement[] =>
  [...root.querySelectorAll("*")].flatMap((el) => [
    ...(el instanceof HTMLVideoElement ? [el] : []),
    ...(el.shadowRoot ? collectVideos(el.shadowRoot) : []),
  ]);

/**
 * The `<video>` of the question on stage. v4 marks every card with
 * `data-question-id`; the stack preloads the next card, so the first video in
 * the DOM is often not the active one. A closed lightbox is not matched (its
 * portal carries no question id). v3 renders inside shadow roots with no
 * such marker, so there the video that is playing is taken instead -- a
 * paused v3 video on stage is not identifiable and the line is left out.
 */
export const findCardVideo = (
  root: Document,
  questionId: string | null | undefined,
): HTMLVideoElement | undefined => {
  const cards = [...root.querySelectorAll("[data-question-id]")];
  if (cards.length > 0) {
    const card = cards.find(
      (el) => el.getAttribute("data-question-id") === questionId,
    );
    // A `lightBox` video is portaled to <body>, outside its card; while the
    // lightbox is open it is the video in the open <dialog>. Only the card on
    // stage can open one, so it belongs to this question.
    return (
      card?.querySelector("video") ??
      root.querySelector<HTMLVideoElement>("dialog[open] video") ??
      undefined
    );
  }
  return collectVideos(root).find((video) => !video.paused);
};

const seconds = (value: number): string =>
  Number.isFinite(value) ? `${value.toFixed(1)} s` : "unknown";

const bufferedAhead = (video: HTMLVideoElement): number => {
  const {buffered, currentTime} = video;
  for (let i = 0; i < buffered.length; i += 1) {
    if (buffered.start(i) <= currentTime && currentTime <= buffered.end(i)) {
      return buffered.end(i) - currentTime;
    }
  }
  return 0;
};

/**
 * One line that tells a stuck video from a paused one and from a playlist
 * that promised more than it holds (#1304): position against duration, the
 * play state, `readyState` (below 3 = waiting for data) and how much is
 * buffered past the playhead. `undefined` when the card has no video, so the
 * line is simply absent.
 */
export const formatVideoLine = (
  video: HTMLVideoElement | undefined,
): string | undefined => {
  if (!video) return undefined;
  const state = video.ended ? "ended" : video.paused ? "paused" : "playing";
  return `Video: ${seconds(video.currentTime)} of ${seconds(video.duration)}, ${state}, readyState ${video.readyState}, buffered ahead ${seconds(bufferedAhead(video))}`;
};

/**
 * Orientation and connectivity: the two things a layout report and a
 * "nothing loads" report are asked first.
 */
export const formatConnectivityLines = ({
  orientation,
  online,
}: {
  orientation: string | undefined;
  online: boolean;
}): string =>
  `Screen orientation: ${orientation ?? "unavailable"}\nOnline: ${online ? "yes" : "no"}`;
